import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { HotTable } from '@handsontable/react-wrapper'
import { registerAllModules } from 'handsontable/registry'
import 'handsontable/styles/handsontable.css'
import 'handsontable/styles/ht-theme-horizon.css'
import { cable } from '../../cable.js'
import { HOT_LICENSE_KEY } from '../utils/handsontableLicense.js'
import { sanitizeHandsontableHtml } from '../utils/handsontableSanitizer.js'

registerAllModules()

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const HEADER_ROW_COUNT = 5
const REALTIME_DEBOUNCE = 1500

function formatMoney(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return ''
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatMonthDate(valuationDate, index) {
  if (!valuationDate) return ''
  const start = new Date(`${valuationDate}T00:00:00Z`)
  if (Number.isNaN(start.getTime())) return ''
  const d = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + index, 1))
  return `${MONTH_LABELS[d.getUTCMonth()]}-${String(d.getUTCFullYear()).slice(-2)}`
}

function buildHeaderRows(valuationDate, months) {
  if (months === 0) return []
  const emptyPeriods = Array.from({ length: months }, () => '')
  const yearRow = ['Year', '', '', ...Array.from({ length: months }, (_, i) => `Year ${Math.floor(i / 12) + 1}`)]
  const quarterRow = ['Quarter', '', '', ...Array.from({ length: months }, (_, i) => `Quarter ${Math.floor(i / 3) + 1}`)]
  const monthRow = ['Month', '', '', ...Array.from({ length: months }, (_, i) => `Month ${i + 1}`)]
  const dateRow = ['Period End', '', '', ...Array.from({ length: months }, (_, i) => formatMonthDate(valuationDate, i))]
  const colHeaderRow = ['Tenant / Unit', 'Market Rent', 'Growth Rate', ...emptyPeriods]
  return [yearRow, quarterRow, monthRow, dateRow, colHeaderRow]
}

export default function MarketRentGrowthTable({ valuationId, dataUrl }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [months, setMonths] = useState(0)
  const [valuationDate, setValuationDate] = useState(null)
  const [rows, setRows] = useState([])
  const fetchRef = useRef(null)

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    setError(null)
    try {
      const res = await fetch(dataUrl, { headers: { Accept: 'application/json' } })
      const json = await res.json()
      if (!res.ok || !json.success) throw new Error(json.error || `Request failed (${res.status})`)
      const data = json.data || {}
      setMonths(data.months || 0)
      setValuationDate(data.valuation_date || null)
      setRows(data.rows || [])
    } catch (err) {
      setError(err.message || 'Failed to load')
    } finally {
      if (!silent) setLoading(false)
    }
  }, [dataUrl])

  useEffect(() => { fetchRef.current = fetchData }, [fetchData])

  useEffect(() => { fetchData() }, [fetchData])

  // Subscribe to ValuationChannel for live recalculation when any related entity changes
  useEffect(() => {
    if (!valuationId) return undefined
    let debounceTimer = null

    const subscription = cable.subscriptions.create(
      { channel: 'ValuationChannel', valuation_id: valuationId },
      {
        received() {
          if (debounceTimer) clearTimeout(debounceTimer)
          debounceTimer = setTimeout(() => {
            fetchRef.current?.(true)
          }, REALTIME_DEBOUNCE)
        }
      }
    )

    return () => {
      subscription.unsubscribe()
      if (debounceTimer) clearTimeout(debounceTimer)
    }
  }, [valuationId])

  const headerRows = useMemo(() => buildHeaderRows(valuationDate, months), [valuationDate, months])

  const tableData = useMemo(() => {
    const data = rows.map(row => [
      row.tenant_label,
      formatMoney(row.market_rent_pa),
      row.growth_rate_name,
      ...(row.monthly || []).map(formatMoney)
    ])
    return [...headerRows, ...data]
  }, [headerRows, rows])

  const mergeCells = useMemo(() => (
    // Rows 0..3 (Year / Quarter / Month / Date) — merge leading 3 columns so the
    // row-type label anchors to the right edge next to the period cells.
    [0, 1, 2, 3].map(r => ({ row: r, col: 0, rowspan: 1, colspan: 3 }))
  ), [])

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    )
  }

  if (error) {
    return <div className="alert alert-danger m-3">{error}</div>
  }

  if (rows.length === 0) {
    return (
      <div className="text-muted text-center py-5">
        No projections to show. Assign an MLA with a market rent growth rate to see tenants here.
      </div>
    )
  }

  return (
    <div className="cash-flow-table-container">
      <div className="cash-flow-table-viewport">
        <HotTable
          data={tableData}
          rowHeaders={false}
          colHeaders={false}
          fixedColumnsStart={3}
          themeName="ht-theme-horizon"
          licenseKey={HOT_LICENSE_KEY}
          sanitizer={sanitizeHandsontableHtml}
          stretchH="none"
          width="100%"
          height="auto"
          readOnly={true}
          manualColumnResize={true}
          manualRowResize={false}
          mergeCells={mergeCells}
          cells={(row, col) => {
            if (row < HEADER_ROW_COUNT - 1) {
              // merged row-type label rows: right-align the spanning cell
              return { readOnly: true, className: 'htRight htMiddle' }
            }
            if (row < HEADER_ROW_COUNT) {
              // static column header row (Tenant / Unit | Market Rent | Growth Rate)
              if (col < 3) return { readOnly: true, className: 'htLeft htMiddle' }
              return { readOnly: true, className: 'htRight htMiddle' }
            }
            if (col < 3) {
              return { readOnly: true, className: col === 1 ? 'htRight' : 'htLeft' }
            }
            return { readOnly: true, className: 'htRight' }
          }}
        />
      </div>
    </div>
  )
}
